<template>
  <div>
    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <b-table
        ref="refWorkplaceListTable"
        style="min-height: 70vh"
        :sticky-header="true"
        class="position-relative workplace-table"
        :items="fetchWorkplaces"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Nenhum registro correspondente encontrado"
      >
        <template #head(blocked_at)="{ label }">
          {{ label }}
          <app-help
            title="Ajuda: Bloqueio Lançamento"
            description="Essa opção informa se a unidade foi bloqueada para lançamentos que tenham data e hora maior que a informada."
          />
        </template>
        <template #head(physician_duty_launch_mode)="{ label }">
          {{ label }}
          <app-help
            title="Ajuda: Tipo Lançamento"
            description="Identifica como o plantão da unidade será inserido no sistema, podendo ser através do próprio sistema da Imediato, por integração, ou uma combinação de ambos."
          />
        </template>
        <template #head(contract_effective_date)="{ label }">
          {{ label }}
          <app-help
            title="Ajuda: Vigência contrato"
            description="Permite que a imediato insira uma data de vigência do cliente com a empresa que ele fornece serviço."
          />
        </template>
        <template #head(can_advance)="{ label }">
          {{ label }}
          <app-help
            title="Ajuda: Pode Adiantar"
            description="Permite que a Imediato libere ou bloqueie a antecipação de plantões de uma unidade específica."
          />
        </template>
        <template #head(request_enabled_at)="{ label }">
          {{ label }}
          <app-help
            title="Ajuda: Início Lançamento"
            description="Controlar o lançamento por parte da empresa cliente, garantindo que não sejam inseridos plantões com datas anteriores à permitida pela Imediato. Essa data é obrigatória e a ausência dessa informação no sistema impede o cliente de fazer qualquer lançamento."
          />
        </template>

        <template #cell(name)="{ item }">
          {{ item.name }}
          <b-badge
            v-if="item.is_new"
            pill
            variant="light-success"
          >
            Nova
          </b-badge>
        </template>

        <template #cell(due_date_rules)="{ item }">
          <div class="text-nowrap">
            <strong v-if="item.due_date_rules && item.due_date_rules[0]">
              Dia fixo {{ item.due_date_rules[0].due_day }}
            </strong>
            <ul class="rules-list">
              <li
                v-for="rule in item.due_date_rules"
                :key="rule.id"
              >
                De <strong>{{ rule.start_day }}</strong> a <strong>{{ rule.end_day }}</strong> somar <strong>{{ rule.months_to_add }}</strong>
              </li>
            </ul>
          </div>
        </template>

        <template #cell(physician_duty_launch_mode)="data">
          <b-badge
            pill
            :variant="`light-${getLaunchMode(data.item.physician_duty_launch_mode).variant}`"
            class="text-capitalize"
          >
            {{ getLaunchMode(data.item.physician_duty_launch_mode).name }}
          </b-badge>
        </template>

        <template #cell(request_enabled_at)="data">
          <div v-if="data.item.request_enabled_at">
            {{ formatDateTimeDDMMYYYY(data.item.request_enabled_at) }}
          </div>
          <b-badge
            v-else
            pill
            variant="light-danger"
            class="text-capitalize"
          >
            Bloqueado
          </b-badge>
        </template>

        <template #cell(blocked_at)="data">
          <b-badge
            v-if="data.item.blocked_at"
            pill
            variant="light-danger"
            class="text-capitalize"
          >
            {{ formatDateTimeDDMMYYHHMMSS(data.item.blocked_at) }}
          </b-badge>
          <div v-else>
            Não
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <b-dropdown
            variant="link"
            no-caret
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              :disabled="!$can('manage', 'Institution')"
              :to="{ name: 'workplace-view', params: { institutionId: institutionId, id: item.id } }"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">Detalhes</span>
            </b-dropdown-item>
            <template v-if="!item.is_new">
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                @click="showLockEnableForRequest(item)"
              >
                <feather-icon icon="LockIcon" />
                <span class="align-middle ml-50">Editar/Início Lançamento</span>
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                @click="showBlocked(item)"
              >
                <feather-icon icon="LockIcon" />
                <span class="align-middle ml-50">Bloquear Lançamento</span>
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                @click="showLaunchMode(item)"
              >
                <feather-icon icon="SendIcon" />
                <span class="align-middle ml-50">Editar T. Lançamento</span>
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                @click="showPayrollValueEdit(item)"
              >
                <feather-icon icon="DollarSignIcon" />
                <span class="align-middle ml-50">Editar Valor Folha</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="item.can_advance"
                :disabled="!$can('manage', 'Institution')"
                @click="showCanAdvance(item, false)"
              >
                <feather-icon icon="CircleIcon" />
                <span class="align-middle ml-50">Desabilitar antecipação</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="!item.can_advance"
                :disabled="!$can('manage', 'Institution')"
                @click="showCanAdvance(item, true)"
              >
                <feather-icon icon="CircleIcon" />
                <span class="align-middle ml-50">Habilitar antecipação</span>
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                @click="showContractEffectiveDate(item)"
              >
                <feather-icon icon="ClockIcon" />
                <span class="align-middle ml-50">Alterar vigência contrato</span>
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                @click="showDueDateRulesModalEdit(item)"
              >
                <feather-icon icon="CalendarIcon" />
                <span class="align-middle ml-50">Editar Vencimento</span>
              </b-dropdown-item>
              <b-dropdown-item
                :disabled="!$can('manage', 'Institution')"
                :to="{ name: 'institution-edit-workplace-cnpj', params: { id: institutionFilter, workplaceId: item.id } }"
              >
                <feather-icon icon="UsersIcon" />
                <span class="align-middle ml-50">Aprovações CNPJ</span>
              </b-dropdown-item>
            </template>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>

    <workplace-due-date-rules-modal
      ref="workplaceDueDateRulesModal"
      @refetch-data="refetchData"
    />
    <workplace-modal
      ref="workplaceModal"
      @refetch-data="refetchData"
    />
    <workplace-blocked-modal
      ref="workplaceBlockedModal"
      @refetch-data="refetchData"
    />
    <workplace-lock-modal
      ref="workplaceLockModal"
      @refetch-data="refetchData"
    />
    <workplace-launch-modal
      ref="workplaceLaunchModal"
      @refetch-data="refetchData"
    />
    <workplace-payroll-value-modal
      ref="workplacePayrollValueModal"
      @refetch-data="refetchData"
    />
    <workplace-contract-effective-date-modal
      ref="contractEffectiveDateModal"
      @refetch-data="refetchData"
    />
    <workplace-can-advance-modal
      ref="canAdvanceModal"
      @refetch-data="refetchData"
    />
  </div>
</template>

<script>
import {
  BBadge,
  BCard,
  BDropdown,
  BDropdownItem,
  BTable,
} from 'bootstrap-vue'
import store from '@/store'
import {
  onUnmounted, ref,
} from '@vue/composition-api'
import institutionStoreModule from '@/views/institution/institutionStoreModule'
import { formatDateTimeDDMMYYHHMMSS, formatDateTimeDDMMYYYY } from '@core/utils/utils'
import AppHelp from '@core/components/app-help/AppHelp.vue'
import useWorkplaceList from './useWorkplaceList'
import WorkplaceLockModal from './workplace-lock/WorkplaceLockModal.vue'
import WorkplaceDueDateRulesModal from './workplace-due-date-rules/WorkplaceDueDateRulesModal.vue'
import WorkplaceModal from './workplace/WorkplaceModal.vue'
import WorkplaceBlockedModal from './workplace-blocked/WorkplaceBlockedModal.vue'
import WorkplaceLaunchModal from './workplace-launch/WorkplaceLaunchModal.vue'
import WorkplacePayrollValueModal from './workplace-payroll-value/WorkplacePayrollValueModal.vue'
import WorkplaceContractEffectiveDateModal from './workplace-contract-effective-date/WorkplaceContractEffectiveDateModal.vue'
import WorkplaceCanAdvanceModal from './workplace-can-advance/WorkplaceCanAdvanceModal.vue'

export default {
  components: {
    WorkplaceCanAdvanceModal,
    WorkplaceContractEffectiveDateModal,
    WorkplacePayrollValueModal,
    WorkplaceLaunchModal,
    WorkplaceBlockedModal,
    WorkplaceModal,
    WorkplaceDueDateRulesModal,
    WorkplaceLockModal,
    AppHelp,
    BBadge,
    BDropdown,
    BDropdownItem,
    BCard,
    BTable,
  },
  props: {
    institutionId: {
      type: String,
      required: true,
    },
  },
  setup({ institutionId }) {
    const INSTITUTION_APP_STORE_MODULE_NAME = 'app-institution'

    // Register module
    if (!store.hasModule(INSTITUTION_APP_STORE_MODULE_NAME)) store.registerModule(INSTITUTION_APP_STORE_MODULE_NAME, institutionStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(INSTITUTION_APP_STORE_MODULE_NAME)) store.unregisterModule(INSTITUTION_APP_STORE_MODULE_NAME)
    })
    const workplaceModal = ref(null)
    const workplaceBlockedModal = ref(null)
    const contractEffectiveDateModal = ref(null)
    const canAdvanceModal = ref(null)
    const workplaceLockModal = ref(null)
    const workplaceLaunchModal = ref(null)
    const workplacePayrollValueModal = ref(null)
    const workplaceDueDateRulesModal = ref(null)

    const {
      fetchWorkplaces,
      refWorkplaceListTable,
      refetchData,
      institutionFilter,
      tableColumns,
      getLaunchMode,
    } = useWorkplaceList()

    institutionFilter.value = institutionId

    function showLockEnableForRequest(workplace) {
      workplaceLockModal.value.show(workplace)
    }

    function showBlocked(workplace) {
      workplaceBlockedModal.value.show(workplace)
    }

    function showContractEffectiveDate(workplace) {
      contractEffectiveDateModal.value.show(workplace)
    }

    function showCanAdvance(workplace, canAdvance) {
      canAdvanceModal.value.show(workplace, canAdvance)
    }

    function showLaunchMode(workplace) {
      workplaceLaunchModal.value.show(workplace)
    }

    function showPayrollValueEdit(workplace) {
      workplacePayrollValueModal.value.show(workplace)
    }

    function showDueDateRulesModalEdit(workplace) {
      workplaceDueDateRulesModal.value.show(workplace)
    }

    return {
      showBlocked,
      workplaceBlockedModal,
      workplaceLaunchModal,
      showLaunchMode,
      showPayrollValueEdit,
      workplacePayrollValueModal,
      getLaunchMode,
      showLockEnableForRequest,
      workplaceLockModal,
      workplaceModal,
      showContractEffectiveDate,
      contractEffectiveDateModal,
      showCanAdvance,
      canAdvanceModal,
      tableColumns,
      fetchWorkplaces,
      refWorkplaceListTable,
      refetchData,
      institutionFilter,
      formatDateTimeDDMMYYHHMMSS,
      formatDateTimeDDMMYYYY,
      workplaceDueDateRulesModal,
      showDueDateRulesModalEdit,
    }
  },
}
</script>

<style lang="scss" scoped>
.rules-list {
  margin-bottom: 0!important;
  padding-inline-start: 20px;
  line-height: 15px;
}

.per-page-selector {
  width: 90px;
}

.physician-class {
  width: 15px;
  height: 15px;
  border-spacing: 0!important;
  margin-top: 2px;
}

.workplace-table {
  min-height: 250px;
}
</style>

<style lang="scss">
.request-enabled-at {
  width: 230px;
}
</style>
